import type { ReactNode } from "react";

/**
 * Renderizador de Markdown acotado al subconjunto que produce Gertrudis:
 * encabezados, negrita, itálica, código, listas y enlaces.
 *
 * Construye elementos React en vez de inyectar HTML. La respuesta del LLM es
 * texto no confiable — con `dangerouslySetInnerHTML` un insight que contenga
 * `<img onerror=...>` se ejecutaría en el dashboard. Acá el peor caso es que un
 * marcador quede sin interpretar y se vea literal.
 *
 * No usa librería externa a propósito: el build de producción corre
 * `pnpm install --frozen-lockfile`, así que sumar una dependencia obliga a
 * regenerar el lockfile para ganar un parser mucho más grande del necesario.
 */

const BOLD = /\*\*(.+?)\*\*/;
const ITALIC = /(?<![\w*])\*([^*\n]+?)\*(?![\w*])/;
const CODE = /`([^`\n]+?)`/;
const LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/;

/** Aplica el formato inline de forma recursiva, del marcador más externo al más interno. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  if (!text) return [];

  const rules: Array<[RegExp, (match: RegExpMatchArray, key: string) => ReactNode]> = [
    [CODE, (match, key) => <code key={key}>{match[1]}</code>],
    [LINK, (match, key) => (
      <a key={key} href={match[2]} target="_blank" rel="noreferrer noopener">{match[1]}</a>
    )],
    [BOLD, (match, key) => <strong key={key}>{inline(match[1], `${key}b`)}</strong>],
    [ITALIC, (match, key) => <em key={key}>{inline(match[1], `${key}i`)}</em>],
  ];

  for (const [pattern, render] of rules) {
    const match = text.match(pattern);
    if (match && match.index !== undefined) {
      return [
        ...inline(text.slice(0, match.index), `${keyPrefix}p`),
        render(match, `${keyPrefix}m`),
        ...inline(text.slice(match.index + match[0].length), `${keyPrefix}s`),
      ];
    }
  }
  return [text];
}

interface Block {
  type: "h" | "p" | "ul" | "ol" | "hr" | "table";
  level?: number;
  lines: string[];
}

function toBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  let list: Block | null = null;
  let table: Block | null = null;

  for (const raw of source.split("\n")) {
    const line = raw.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      list = null;
      table = null;
      continue;
    }

    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      list = null;
      if (!table) {
        table = { type: "table", lines: [] };
        blocks.push(table);
      }
      table.lines.push(trimmed);
      continue;
    }
    table = null;

    if (/^(-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
      list = null;
      blocks.push({ type: "hr", lines: [] });
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      list = null;
      blocks.push({ type: "h", level: heading[1].length, lines: [heading[2]] });
      continue;
    }

    const bullet = trimmed.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      if (list?.type !== "ul") {
        list = { type: "ul", lines: [] };
        blocks.push(list);
      }
      list.lines.push(bullet[1]);
      continue;
    }

    const numbered = trimmed.match(/^\d+[.)]\s+(.*)$/);
    if (numbered) {
      if (list?.type !== "ol") {
        list = { type: "ol", lines: [] };
        blocks.push(list);
      }
      list.lines.push(numbered[1]);
      continue;
    }

    // Párrafo: se acumulan líneas contiguas para no romper una idea en dos.
    const previous = blocks[blocks.length - 1];
    if (list === null && previous?.type === "p") {
      previous.lines.push(trimmed);
    } else {
      list = null;
      blocks.push({ type: "p", lines: [trimmed] });
    }
  }
  return blocks;
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks = toBlocks(source ?? "");
  return (
    <div className={className}>
      {blocks.map((block, index) => {
        const key = `b${index}`;
        if (block.type === "hr") return <hr key={key} />;
        if (block.type === "h") {
          const Tag = (`h${Math.min(6, (block.level ?? 2) + 1)}`) as "h3";
          return <Tag key={key}>{inline(block.lines[0], key)}</Tag>;
        }
        if (block.type === "ul" || block.type === "ol") {
          const Tag = block.type === "ul" ? "ul" : "ol";
          return (
            <Tag key={key}>
              {block.lines.map((line, itemIndex) => (
                <li key={`${key}i${itemIndex}`}>{inline(line, `${key}i${itemIndex}`)}</li>
              ))}
            </Tag>
          );
        }
        if (block.type === "table") {
          const parseRow = (lineStr: string) =>
            lineStr
              .slice(1, -1)
              .split("|")
              .map((c) => c.trim());
          const [rawHeader, separator, ...rawRows] = block.lines;
          const isSeparator = separator && /^[\s\-:|]+$/.test(separator.replace(/\|/g, ""));
          const headers = rawHeader ? parseRow(rawHeader) : [];
          const rows = isSeparator ? rawRows.map(parseRow) : [parseRow(separator), ...rawRows.map(parseRow)];

          return (
            <div key={key} className="table-responsive" style={{ margin: "1rem 0", overflowX: "auto" }}>
              <table className="compact-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                {headers.length > 0 && (
                  <thead>
                    <tr>
                      {headers.map((h, hIdx) => (
                        <th key={hIdx} style={{ textAlign: "left", padding: "6px 10px", borderBottom: "1px solid var(--border-color, #333)" }}>
                          {inline(h, `${key}th${hIdx}`)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {rows.map((r, rIdx) => (
                    <tr key={rIdx}>
                      {r.map((cell, cIdx) => {
                        const pctMatch = cell.trim().match(/^(\d+(?:\.\d+)?)\s*%$/);
                        return (
                          <td key={cIdx} style={{ padding: "6px 10px", borderBottom: "1px solid var(--border-subtle, #222)" }}>
                            {pctMatch ? (
                              <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", width: "100%", minWidth: "100px" }}>
                                <div style={{ flex: 1, height: "6px", background: "rgba(255,255,255,0.08)", borderRadius: "3px", overflow: "hidden" }}>
                                  <div
                                    style={{
                                      width: `${Math.min(100, Math.max(0, parseFloat(pctMatch[1])))}%`,
                                      height: "100%",
                                      background: "var(--blue, #7d96ff)",
                                      borderRadius: "3px",
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: "12px", minWidth: "42px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                                  {cell.trim()}
                                </span>
                              </div>
                            ) : (
                              inline(cell, `${key}td${rIdx}_${cIdx}`)
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return <p key={key}>{block.lines.map((line, i) => inline(line, `${key}l${i}`))}</p>;
      })}
    </div>
  );
}
